/**
 * Reordering rows and columns in a table.
 *
 * `@tiptap/extension-table` ships no move command, but prosemirror-tables
 * does, so this extension is only the surface: four commands, their keymap,
 * and the one rule markdown imposes. A markdown table's first row is its
 * header, and a row moves as a whole node (its `th` cells travel with it), so
 * the header row is pinned at index 0 and no body row may pass above it.
 *
 * A move acts on the row/column holding the cursor and carries the cursor
 * along with it, so the shortcut can be pressed again to walk a row up a
 * table and typing after a move edits the cell rather than replacing it.
 * Columns need no header rule: every cell in row 0 is a `th`, so any
 * permutation of the columns leaves it a header row.
 */
import { Extension, type CommandProps } from "@tiptap/core";
import type { EditorState, Transaction } from "@tiptap/pm/state";
import { TextSelection } from "@tiptap/pm/state";
import {
  isInTable,
  moveTableColumn,
  moveTableRow,
  rowIsHeader,
  selectionCell,
  TableMap,
} from "@tiptap/pm/tables";
import { invariant } from "ts-invariant";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableMove: {
      /** Swap the cursor's row with the one above it. */
      moveRowUp: () => ReturnType;
      /** Swap the cursor's row with the one below it. */
      moveRowDown: () => ReturnType;
      /** Swap the cursor's column with the one to its left. */
      moveColumnLeft: () => ReturnType;
      /** Swap the cursor's column with the one to its right. */
      moveColumnRight: () => ReturnType;
    };
  }
}

/** The rectangle of the cell holding the cursor. Not `selectedRect`, which
 *  widens to the whole selection: a move always acts on one row and one
 *  column, including the row the last move left selected. */
function cursorCell(state: EditorState) {
  const $cell = selectionCell(state);
  const table = $cell.node(-1);
  const tableStart = $cell.start(-1);
  const map = TableMap.get(table);
  return { ...map.findCell($cell.pos - tableStart), map, table, tableStart };
}

/** Index the cursor's row moves to, or null when it can't go that way: off
 *  the end of the table, or above the header row (which never moves). */
function rowTarget(state: EditorState, dir: 1 | -1): number | null {
  if (!isInTable(state)) return null;
  const { top, bottom, map, table } = cursorCell(state);
  const firstBody = rowIsHeader(map, table, 0) ? 1 : 0;
  if (top < firstBody) return null;
  const target = dir === -1 ? top - 1 : bottom;
  return target < firstBody || target >= map.height ? null : target;
}

/** Index the cursor's column moves to, or null at the table's edge. */
function columnTarget(state: EditorState, dir: 1 | -1): number | null {
  if (!isInTable(state)) return null;
  const { left, right, map } = cursorCell(state);
  const target = dir === -1 ? left - 1 : right;
  return target < 0 || target >= map.width ? null : target;
}

/** Drop the cursor into the cell the move carried to (`row`, `col`). The
 *  table occupies the same range it did, so `tableStart` still finds it. */
function keepCursor(
  tr: Transaction,
  tableStart: number,
  row: number,
  col: number,
): Transaction {
  const table = tr.doc.nodeAt(tableStart - 1);
  invariant(table, "a moved row leaves the table where it was");
  const cell = TableMap.get(table).positionAt(row, col, table);
  return tr.setSelection(
    TextSelection.near(tr.doc.resolve(tableStart + cell + 1)),
  );
}

const rowMove =
  (dir: 1 | -1) =>
  () =>
  ({ state, dispatch }: CommandProps) => {
    const to = rowTarget(state, dir);
    if (to === null) return false;
    const { top, left, tableStart } = cursorCell(state);
    return moveTableRow({ from: top, to, select: false })(
      state,
      dispatch && ((tr) => dispatch(keepCursor(tr, tableStart, to, left))),
    );
  };

const columnMove =
  (dir: 1 | -1) =>
  () =>
  ({ state, dispatch }: CommandProps) => {
    const to = columnTarget(state, dir);
    if (to === null) return false;
    const { top, left, tableStart } = cursorCell(state);
    return moveTableColumn({ from: left, to, select: false })(
      state,
      dispatch && ((tr) => dispatch(keepCursor(tr, tableStart, top, to))),
    );
  };

export const TableMove = Extension.create({
  name: "tableMove",

  addCommands() {
    return {
      moveRowUp: rowMove(-1),
      moveRowDown: rowMove(1),
      moveColumnLeft: columnMove(-1),
      moveColumnRight: columnMove(1),
    };
  },

  // Alt-Shift is free inside a cell: prosemirror-tables binds plain and
  // Shift-arrows (cell navigation and cell selection), neither with Alt.
  addKeyboardShortcuts() {
    return {
      "Alt-Shift-ArrowUp": () => this.editor.commands.moveRowUp(),
      "Alt-Shift-ArrowDown": () => this.editor.commands.moveRowDown(),
      "Alt-Shift-ArrowLeft": () => this.editor.commands.moveColumnLeft(),
      "Alt-Shift-ArrowRight": () => this.editor.commands.moveColumnRight(),
    };
  },
});
